/**
 * THE PLAYERS LIST — every Tap 'N Win player this business has, and the gifts
 * each has won AT THIS BUSINESS. A business sees nothing of any other business;
 * a super admin can see every business and who is the same person across them.
 * Nothing on this screen writes anything.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Search, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';

const day = (iso) => (!iso ? '—' : String(iso).slice(0, 10));

const Cell = ({ children, className = '' }) => (
    <td className={`px-3 py-2 align-top ${className}`}>{children}</td>
);

export default function GoalTapnwinPlayers() {
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuth();
    const [params] = useSearchParams();
    const orgId = params.get('organization_id') || '';
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState('');
    const [all, setAll] = useState(false);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(null);
    const p = (k) => t(`goalGame.playersList.${k}`);
    const sup = isSuperAdmin();

    const load = useCallback(async () => {
        setBusy(true);
        setError('');
        try {
            const res = await api.get('/goal-game/admin/players-list', {
                params: { organization_id: orgId, q, limit: 200,
                          all_businesses: all && sup },
            });
            setRows(res.data.players || []);
        } catch (_e) {
            setError(p('failed'));
        } finally { setBusy(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, q, all, sup]);

    useEffect(() => { load(); }, [load]);

    const openPlayer = async (row, everywhere = false) => {
        setOpen({ loading: true });
        try {
            const res = await api.get(`/goal-game/admin/players-list/${row.id}`, {
                params: { organization_id: row.organization_id || orgId, everywhere },
            });
            setOpen({ ...res.data, everywhere });
        } catch (_e) {
            setOpen(null);
            setError(p('failed'));
        }
    };

    const reissue = async (code) => {
        const reason = window.prompt('Why is this card being re-issued?');
        if (reason === null) return;
        try {
            const res = await api.post(`/goal-game/admin/cards/${code}/reissue`,
                { organization_id: open?.organization_id || orgId, reason });
            toast.success(`New card ${res.data.new_code}`);
            if (open?.id) await openPlayer({ id: open.id, organization_id: open.organization_id });
        } catch (_e) { toast.error('Could not re-issue'); }
    };

    if (open) {
        const profiles = open.profiles || (open.id ? [open] : []);
        return (
            <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4" data-testid="gg-player-profile">
                <button type="button" onClick={() => setOpen(null)}
                        data-testid="gg-player-back"
                        className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline">
                    <ArrowLeft size={15} />{p('back')}
                </button>
                {open.loading && <Loader2 className="animate-spin" size={18} />}
                {profiles.map((prof) => (
                    <div key={prof.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                        <p className="text-lg font-bold" data-testid="gg-player-name">
                            {prof.screen_name || '—'}
                        </p>
                        <p className="text-sm text-muted-foreground" data-testid="gg-player-email">
                            {prof.email}
                        </p>
                        {prof.organization_name && (
                            <p className="text-xs mt-1 font-semibold" data-testid="gg-player-business">
                                {prof.organization_name}
                            </p>
                        )}
                        <p className="text-xs mt-2">
                            {p('signedUp')}: {day(prof.signed_up_at)}
                            {prof.signed_up_place ? ` (${prof.signed_up_place === 'home' ? p('atHome') : p('onSite')})` : ''} · {p('tiedTo')}:{' '}
                            <span data-testid="gg-player-locations">
                                {(prof.locations || []).join(', ') || '—'}
                            </span>
                        </p>
                        <p className="text-xs mt-1" data-testid="gg-player-meta">
                            {p('lastPlayed')}: {day(prof.last_played_at)}
                            {' · '}{p('referredBy')}: {prof.referred_by || '—'}
                            {(prof.businesses || []).length > 0 && (
                                <> · {p('businesses')}: <span data-testid="gg-player-businesses">
                                    {prof.businesses.join(', ')}</span></>
                            )}
                        </p>
                        {/* GIFT GUARD — admin and manager eyes only: the phone
                            bound to this player, the quiet flags and every
                            re-issue. A player never sees any of this. */}
                        {prof.guard && (
                            <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-xs"
                                 data-testid="gg-player-guard">
                                <p className="font-semibold">Activation</p>
                                <p data-testid="gg-player-guard-phone">
                                    Phone: {prof.guard.activation_phone || '—'}
                                    {prof.guard.bound_at ? ` · since ${day(prof.guard.bound_at)}` : ''}
                                </p>
                                {(prof.guard.flags || []).length > 0 && (
                                    <ul className="mt-1 space-y-0.5" data-testid="gg-player-guard-flags">
                                        {prof.guard.flags.map((f) => (
                                            <li key={f.id} className="text-amber-600">
                                                {f.kind.replace(/_/g, ' ')} · {day(f.at)}
                                                {f.voucher_code ? ` · ${f.voucher_code}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {(prof.guard.reissues || []).length > 0 && (                                    <ul className="mt-1 space-y-0.5" data-testid="gg-player-guard-reissues">
                                        {prof.guard.reissues.map((r) => (
                                            <li key={r.id} className="text-muted-foreground">
                                                {r.old_code} → {r.new_code} · {r.by_name} · {day(r.at)}
                                                {r.reason ? ` · ${r.reason}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {/* THE GIFT-CARD EMAILS: when, to whom, sent or
                                    why not. */}
                                {(prof.guard.sends || []).length > 0 && (
                                    <ul className="mt-1 space-y-0.5" data-testid="gg-player-guard-sends">
                                        {prof.guard.sends.map((s) => (
                                            <li key={`${s.voucher_code}-${s.sent_at}`}
                                                className={s.result === 'sent' ? 'text-emerald-600' : 'text-amber-600'}>
                                                {s.voucher_code} · {s.result === 'sent' ? 'emailed' : s.result}
                                                {s.error ? ` (${String(s.error).slice(0, 60)})` : ''}
                                                {' · '}{s.email} · {day(s.sent_at)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                        <p className="text-sm font-semibold mt-4">{p('profileGifts')}</p>                        {(prof.gifts || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground" data-testid="gg-player-no-gifts">
                                {p('noGifts')}
                            </p>
                        ) : (
                            <table className="w-full text-sm mt-2" data-testid="gg-player-gifts">
                                <thead className="text-xs uppercase text-muted-foreground">
                                    <tr>
                                        <th className="px-3 py-2 text-left">{p('gift')}</th>
                                        <th className="px-3 py-2 text-left">{p('wonOn')}</th>
                                        <th className="px-3 py-2 text-left">{p('location')}</th>
                                        <th className="px-3 py-2 text-left">{p('status')}</th>
                                        <th className="px-3 py-2 text-left">{p('emailed')}</th>
                                        <th className="px-3 py-2 text-left">Activation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prof.gifts.map((g, i) => (
                                        <tr key={i} className="border-t border-slate-100 dark:border-slate-800"
                                            data-testid={`gg-player-gift-${i}`}>
                                            <Cell>
                                                {g.title || '—'}
                                                {g.next_visit && (
                                                    <span className="ml-1 text-[11px] text-amber-600">
                                                        {p('nextVisit')}
                                                    </span>
                                                )}
                                            </Cell>
                                            <Cell>{day(g.won_at)}</Cell>
                                            <Cell>{g.location || '—'}</Cell>
                                            <Cell data-testid={`gg-player-gift-status-${i}`}>
                                                {p(`status_${g.status || 'banked'}`)}
                                            </Cell>
                                            <Cell>{g.emailed ? p('emailed') : p('notEmailed')}</Cell>
                                            {/* GIFT GUARD — only a card that waits
                                                on a text says anything here, and a
                                                re-issue is quiet: never offered to
                                                the player, always logged. */}
                                            <Cell data-testid={`gg-player-gift-guard-${i}`}>
                                                {g.guarded ? (
                                                    <span className="flex flex-wrap items-center gap-2">
                                                        <span className={g.activated_at ? 'text-emerald-600' : 'text-amber-600'}>
                                                            {g.activated_at ? 'Activated' : 'Not activated'}
                                                        </span>
                                                        {g.code && (
                                                            <button type="button"
                                                                    onClick={() => reissue(g.code)}
                                                                    data-testid={`gg-player-reissue-${i}`}
                                                                    className="text-[11px] font-semibold text-slate-500 hover:text-amber-600">
                                                                Re-issue
                                                            </button>
                                                        )}
                                                    </span>
                                                ) : '—'}
                                            </Cell>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        <p className="text-sm font-semibold mt-5">{p('profilePicks')}</p>
                        {(prof.picks || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground" data-testid="gg-player-no-picks">
                                {p('noPicks')}
                            </p>
                        ) : (
                            <table className="w-full text-sm mt-2" data-testid="gg-player-picks">
                                <thead className="text-xs uppercase text-muted-foreground">
                                    <tr>
                                        <th className="px-3 py-2 text-left">{p('playedOn')}</th>
                                        <th className="px-3 py-2 text-left">{p('game')}</th>
                                        <th className="px-3 py-2 text-left">{p('winnerPick')}</th>
                                        <th className="px-3 py-2 text-left">{p('starPick')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prof.picks.map((k, i) => (
                                        <tr key={i} className="border-t border-slate-100 dark:border-slate-800"
                                            data-testid={`gg-player-pick-${i}`}>
                                            <Cell>{day(k.played_on)}</Cell>
                                            <Cell>{k.game || '—'}</Cell>
                                            <Cell>
                                                {k.winner_pick || (k.winner_side ? p(`side_${k.winner_side}`) : '—')}
                                                {k.winner_right && (
                                                    <span className="ml-1 text-[11px] text-emerald-600">
                                                        {p('right')}
                                                    </span>
                                                )}
                                            </Cell>
                                            <Cell>{k.star_pick || '—'}</Cell>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                ))}
                {sup && !open.everywhere && open.id && (
                    <button type="button" onClick={() => openPlayer(open, true)}
                            data-testid="gg-player-everywhere"
                            className="text-sm font-medium text-amber-600 hover:underline">
                        {p('everywhere')}
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-testid="gg-players-page">
            <div className="flex flex-wrap items-center gap-3">
                <Users size={20} />
                <h1 className="text-xl font-bold" data-testid="gg-players-title">{p('title')}</h1>
                <Link to={`/goal-game?organization_id=${orgId}`}
                      data-testid="gg-players-back-control"
                      className="text-sm text-blue-600 hover:underline ml-auto">
                    Tap'N Win
                </Link>
            </div>
            <p className="text-sm text-muted-foreground">{p('subtitle')}</p>
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
                    <input value={q} onChange={(e) => setQ(e.target.value)}
                           placeholder={p('search')} data-testid="gg-players-search"
                           className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 pl-9 pr-3 py-2 text-sm w-72" />
                </div>
                {sup && (
                    <button type="button" onClick={() => setAll(!all)}
                            data-testid="gg-players-scope"
                            className={`rounded-lg px-3 py-2 text-sm font-medium border ${all
                                ? 'bg-amber-500 text-white border-amber-500'
                                : 'border-slate-200 dark:border-slate-700'}`}>
                        {all ? p('allBusinesses') : p('myBusiness')}
                    </button>
                )}
                {busy && <Loader2 className="animate-spin" size={16} />}
            </div>
            {error && <p className="text-sm text-red-600" data-testid="gg-players-error">{error}</p>}
            {!busy && rows.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="gg-players-empty">{p('none')}</p>
            )}
            {rows.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                    <table className="w-full text-sm" data-testid="gg-players-table">
                        <thead className="text-xs uppercase text-muted-foreground bg-slate-50 dark:bg-slate-900/50">
                            <tr>
                                <th className="px-3 py-2 text-left">{p('screenName')}</th>
                                <th className="px-3 py-2 text-left">{p('email')}</th>
                                <th className="px-3 py-2 text-left">{p('signedUp')}</th>
                                <th className="px-3 py-2 text-left">{p('signedUpPlace')}</th>
                                <th className="px-3 py-2 text-left">{p('locations')}</th>
                                <th className="px-3 py-2 text-left">{p('lastPlayed')}</th>
                                <th className="px-3 py-2 text-left">{p('giftsWon')}</th>
                                <th className="px-3 py-2 text-left">{p('giftsOpen')}</th>
                                <th className="px-3 py-2 text-left">{p('referredBy')}</th>
                                {all && sup && <th className="px-3 py-2 text-left">{p('businesses')}</th>}
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={`${r.organization_id}-${r.id}`}
                                    className="border-t border-slate-100 dark:border-slate-800"
                                    data-testid={`gg-player-row-${r.id}`}>
                                    <Cell className="font-semibold">{r.screen_name || '—'}</Cell>
                                    <Cell>{r.email}</Cell>
                                    <Cell>{day(r.signed_up_at)}</Cell>
                                    <Cell>{r.signed_up_place === 'home' ? p('atHome')
                                        : r.signed_up_place === 'site' ? p('onSite') : '—'}</Cell>
                                    <Cell>{(r.locations || []).join(', ') || '—'}</Cell>
                                    <Cell>{day(r.last_played_at)}</Cell>
                                    <Cell data-testid={`gg-player-gifts-${r.id}`}>{r.gifts_won}</Cell>
                                    <Cell>{r.gifts_open}</Cell>
                                    <Cell>{r.referred_by || '—'}</Cell>
                                    {all && sup && (
                                        <Cell data-testid={`gg-player-businesses-${r.id}`}>
                                            {(r.businesses || []).join(', ') || '—'}
                                        </Cell>
                                    )}
                                    <Cell>
                                        <button type="button" onClick={() => openPlayer(r)}
                                                data-testid={`gg-player-open-${r.id}`}
                                                className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-sm font-medium">
                                            {p('open')}
                                        </button>
                                    </Cell>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
